import { useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Pencil } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { PageHeader, SectionCard, Loader, EmptyState, ConfirmDelete, TABLE_HEAD, TABLE_ROW } from "../components/common";
import { ProductDialog } from "../components/ProductDialog";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import { useDepot } from "../context/DepotContext";
import { api, formatApiError, money, qtyFmt } from "../lib/api";

function StockCell({ qty, reorder, unit }) {
  const cls = qty <= 0 ? "text-rose-400" : qty <= reorder ? "text-amber-400" : "text-slate-100";
  return <span className={`font-mono ${cls}`}>{qtyFmt(qty)} <span className="text-xs text-slate-500">{unit}</span></span>;
}

export default function Products() {
  const { isAdmin } = useAuth();
  const { depotParam, depots, scopeLabel } = useDepot();
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState({ open: false, product: null });
  const { data, reload } = useApi("/products", { q, depot_id: depotParam });
  const perDepot = isAdmin && !depotParam;

  const remove = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={scopeLabel} title="Products & Stock" subtitle="Catalog with live stock levels. Amber = at/below reorder level, red = out of stock."
        actions={
          <Button onClick={() => setDialog({ open: true, product: null })} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="add-product-modal-trigger">
            <Plus className="h-4 w-4" /> Add product
          </Button>
        } />
      <SectionCard
        title={`${data?.length ?? 0} products`}
        right={
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, SKU, category" className="pl-9 h-9 bg-slate-950" data-testid="product-search-input" />
          </div>
        }>
        {!data ? <Loader /> : !data.length ? <EmptyState text="No products yet — add your first item" testid="products-empty" /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="products-table">
              <thead className={TABLE_HEAD}>
                <tr>
                  <th className="px-4 py-2.5 text-left">SKU</th>
                  <th className="px-4 py-2.5 text-left">Product</th>
                  <th className="px-4 py-2.5 text-right">Cost</th>
                  <th className="px-4 py-2.5 text-right">Price</th>
                  {perDepot ? depots.map((d) => <th key={d.id} className="px-4 py-2.5 text-right whitespace-nowrap">{d.name}</th>) : null}
                  <th className="px-4 py-2.5 text-right">{perDepot ? "Total" : "In stock"}</th>
                  <th className="px-4 py-2.5 text-right">Reorder</th>
                  {isAdmin && <th className="px-4 py-2.5" />}
                </tr>
              </thead>
              <tbody>
                {data.map((p) => (
                  <tr key={p.id} className={TABLE_ROW} data-testid={`product-row-${p.sku}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-amber-400">{p.sku}</td>
                    <td className="px-4 py-2.5"><div className="font-medium">{p.name}</div><div className="text-xs text-slate-500">{p.category}</div></td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-400">{money(p.cost_price)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{money(p.sell_price)}</td>
                    {perDepot ? depots.map((d) => (
                      <td key={d.id} className="px-4 py-2.5 text-right"><StockCell qty={p.stock?.[d.id] ?? 0} reorder={p.reorder_level} unit={p.unit} /></td>
                    )) : null}
                    <td className="px-4 py-2.5 text-right" data-testid={`product-qty-${p.sku}`}>
                      {perDepot ? <span className="font-mono font-semibold">{qtyFmt(p.qty)}</span> : <StockCell qty={p.qty} reorder={p.reorder_level} unit={p.unit} />}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-slate-500">{qtyFmt(p.reorder_level)}</td>
                    {isAdmin && (
                      <td className="px-2 py-2.5 whitespace-nowrap text-right">
                        <button onClick={() => setDialog({ open: true, product: p })} className="rounded p-1.5 text-slate-500 hover:bg-slate-800 hover:text-slate-100" data-testid={`edit-product-${p.sku}`}>
                          <Pencil className="h-4 w-4" />
                        </button>
                        <ConfirmDelete title={`Delete ${p.name}?`} description="Product and its stock levels will be removed. Past transactions stay in the records." onConfirm={() => remove(p.id)} testid={`delete-product-${p.sku}`} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
      <ProductDialog open={dialog.open} product={dialog.product} onOpenChange={(open) => setDialog((d) => ({ ...d, open }))} onSaved={reload} />
    </div>
  );
}
